import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  message,
  Typography,
  Avatar,
  Spin,
  Space
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import PageLayout from '../../../components/layout/PageLayout';
import masterService from '../../../services/masterService';
import { useUser } from '../../../context/UserContext';

const { Title, Text } = Typography;
const { Option } = Select;

const AddPaymentKey = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [form] = Form.useForm();
  
  const [loading, setLoading] = useState(false);
  const [paymentModes, setPaymentModes] = useState([]);
  const [loadingPaymentModes, setLoadingPaymentModes] = useState(false);

  useEffect(() => {
    fetchPaymentModes();
  }, []);

  const fetchPaymentModes = async () => {
    setLoadingPaymentModes(true);
    try {
      const response = await masterService.getPaymentModes({
        perPage: 1000, // Get all payment modes
        currentPage: 1,
        searchText: ''
      });

      if (response.data && response.data.status) {
        setPaymentModes(response.data.data.data || []);
      } else {
        message.error('Failed to fetch payment modes');
      }
    } catch (error) {
      console.error('Error fetching payment modes:', error);
      message.error('Error fetching payment modes');
    } finally {
      setLoadingPaymentModes(false);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const response = await masterService.addPaymentKey(values);
      
      if (response.data && response.data.status) {
        message.success(response.data.message || 'Payment Key Added Successfully');
        form.resetFields();
        navigate('/payment/key');
      } else {
        message.error(response.data.message || 'Failed to add payment key');
      }
    } catch (error) {
      console.error('Error adding payment key:', error);
      message.error('Error adding payment key');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
  };

  const handleBack = () => {
    navigate('/payment/key');
  };

  const headerContent = (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Avatar
          src={user?.avatar}
          size={64}
          style={{ marginRight: '16px' }}
        >
          {user?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <div>
          <Title level={4} style={{ margin: 0, marginBottom: '4px' }}>
            {user?.name || 'Admin'}
          </Title>
          <Text type="secondary">Add New Payment Key</Text>
        </div>
      </div>
    </div>
  );

  return (
    <PageLayout
      title="Add Payment Key"
      desc="Add a new payment gateway key configuration"
      avatar={user?.avatar}
      headerContent={headerContent}
    >
      <Spin spinning={loading}>
        <Card>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              pm_id: null,
              api_key: null,
              api_secret: null,
              access_code: null,
              payment_url: null,
              merchant_id: null,
              extra1: null,
              extra2: null,
              extra3: null,
            }}
          >
            <Form.Item
              label="Payment Mode"
              name="pm_id"
              rules={[
                { required: true, message: 'Please select payment mode' }
              ]}
            >
              <Select
                placeholder="Please select payment mode"
                loading={loadingPaymentModes}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {paymentModes.map(mode => (
                  <Option key={mode.pm_id} value={mode.pm_id}>
                    {mode.pm_payment_mode}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="API Key"
              name="api_key"
              rules={[
                { required: true, message: 'Please enter API key' }
              ]}
            >
              <Input placeholder="Enter API key" />
            </Form.Item>

            <Form.Item
              label="API Secret Key"
              name="api_secret"
              rules={[
                { required: true, message: 'Please enter API secret key' }
              ]}
            >
              <Input.Password placeholder="Enter API secret key" />
            </Form.Item>

            <Form.Item
              label="Access Code"
              name="access_code"
              rules={[
                { required: true, message: 'Please enter access code' }
              ]}
            >
              <Input placeholder="Enter access code" />
            </Form.Item>

            <Form.Item
              label="Payment URL"
              name="payment_url"
              rules={[
                { required: true, message: 'Please enter payment URL' }
              ]}
            >
              <Input placeholder="Enter payment URL" />
            </Form.Item>

            <Form.Item
              label="Merchant ID"
              name="merchant_id"
              rules={[
                { required: true, message: 'Please enter merchant ID' }
              ]}
            >
              <Input placeholder="Enter merchant ID" />
            </Form.Item>

            <Form.Item
              label="Extra 1"
              name="extra1"
            >
              <Input placeholder="Enter extra field 1 (optional)" />
            </Form.Item>

            <Form.Item
              label="Extra 2"
              name="extra2"
            >
              <Input placeholder="Enter extra field 2 (optional)" />
            </Form.Item>

            <Form.Item
              label="Extra 3"
              name="extra3"
            >
              <Input placeholder="Enter extra field 3 (optional)" />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={loading}
                >
                  Add Key
                </Button>
                <Button
                  onClick={handleReset}
                  icon={<ReloadOutlined />}
                >
                  Reset
                </Button>
                <Button
                  onClick={handleBack}
                  icon={<ArrowLeftOutlined />}
                >
                  Go Back
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </Spin>
    </PageLayout>
  );
};

export default AddPaymentKey;